import React, { useState, useEffect } from 'react';
import axios from '../api/client';
import { useAuth } from '../context/AuthContext';
import {
  Mail, Settings, Users, Building2, Send, CheckCircle, XCircle,
  Plus, Trash2, Save, RefreshCw, Bell, BellOff, ChevronDown, ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';

export default function EmailSettings() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [bmsDepartments, setBmsDepartments] = useState([]);
  const [emailLogs, setEmailLogs] = useState([]);
  const [showLogs, setShowLogs] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testName, setTestName] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [expandedDepts, setExpandedDepts] = useState({});

  const isAdmin = user?.role === 'admin' || user?.role === 'qa_manager';

  useEffect(() => {
    if (isAdmin) {
      fetchSettings();
    }
  }, [isAdmin]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/email-settings');
      setSettings(response.data.settings);
      setAvailableUsers(response.data.available_users || []);
      setBmsDepartments(response.data.bms_departments || []);
    } catch (error) {
      console.error('Error fetching email settings:', error);
      toast.error('Failed to load email settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmailLogs = async () => {
    try {
      const response = await axios.get('/email-settings/logs?limit=50');
      setEmailLogs(response.data);
      setShowLogs(true);
    } catch (error) {
      console.error('Error fetching email logs:', error);
      toast.error('Failed to load email logs');
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      await axios.put('/email-settings', settings);
      toast.success('Email settings saved successfully');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save email settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmail) {
      toast.error('Please enter an email address');
      return;
    }
    try {
      setSendingTest(true);
      console.log('Sending test email to:', testEmail);
      const response = await axios.post('/email-settings/test', {
        email: testEmail,
        name: testName || 'Test Recipient'
      });
      console.log('Test email response:', response.data);
      if (response.data.success) {
        toast.success(response.data.message);
        setTestEmail('');
        setTestName('');
      } else {
        toast.error(response.data.message || 'Failed to send test email');
      }
    } catch (error) {
      console.error('Error sending test email:', error);
      const errorMsg = error.response?.data?.detail || error.userMessage || 'Failed to send test email';
      toast.error(errorMsg);
    } finally {
      setSendingTest(false);
    }
  };

  const handleQAManagerChange = (userId) => {
    const selectedUser = availableUsers.find(u => u.id === userId);
    if (selectedUser) {
      setSettings({
        ...settings,
        qa_manager_user_id: selectedUser.id,
        qa_manager_name: selectedUser.name,
        qa_manager_email: selectedUser.email
      });
    } else {
      setSettings({
        ...settings,
        qa_manager_user_id: null,
        qa_manager_name: null,
        qa_manager_email: null
      });
    }
  };

  const handleDepartmentManagerChange = (deptCode, userId) => {
    const selectedUser = availableUsers.find(u => u.id === userId);
    const dept = bmsDepartments.find(d => d.code === deptCode);
    
    const updatedDeptSettings = [...(settings.department_settings || [])];
    const existingIndex = updatedDeptSettings.findIndex(d => d.department_code === deptCode);
    
    const deptSetting = {
      department_code: deptCode,
      department_name: dept?.name || deptCode,
      manager_user_id: selectedUser?.id || null,
      manager_name: selectedUser?.name || null,
      manager_email: selectedUser?.email || null,
      additional_recipients: existingIndex >= 0 ? updatedDeptSettings[existingIndex].additional_recipients : []
    };
    
    if (existingIndex >= 0) {
      updatedDeptSettings[existingIndex] = deptSetting;
    } else {
      updatedDeptSettings.push(deptSetting);
    }
    
    setSettings({
      ...settings,
      department_settings: updatedDeptSettings
    });
  };

  const getDepartmentManager = (deptCode) => {
    const deptSetting = settings?.department_settings?.find(d => d.department_code === deptCode);
    return deptSetting?.manager_user_id || '';
  };

  const toggleDeptExpand = (deptCode) => {
    setExpandedDepts(prev => ({
      ...prev,
      [deptCode]: !prev[deptCode]
    }));
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white">Access Denied</h2>
          <p className="text-slate-400 mt-2">Only administrators can access email settings.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <RefreshCw className="h-8 w-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-600/20 rounded-xl">
              <Mail className="h-8 w-8 text-blue-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Email Notification Settings</h1>
              <p className="text-slate-400">Configure who receives email notifications for QMS workflows</p>
            </div>
          </div>
          <button
            onClick={handleSaveSettings}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            data-testid="save-email-settings-btn"
          >
            {saving ? <RefreshCw className="h-5 w-5 animate-spin" /> : <Save className="h-5 w-5" />}
            Save Settings
          </button>
        </div>

        {/* QA Manager Section */}
        <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
          <div className="p-4 bg-gradient-to-r from-purple-600/20 to-blue-600/20 border-b border-slate-700">
            <div className="flex items-center gap-3">
              <Users className="h-5 w-5 text-purple-400" />
              <h2 className="text-lg font-semibold text-white">QA Manager</h2>
            </div>
            <p className="text-sm text-slate-400 mt-1">
              The QA Manager receives final approval requests and important quality notifications
            </p>
          </div>
          <div className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Select QA Manager
                </label>
                <select
                  value={settings?.qa_manager_user_id || ''}
                  onChange={(e) => handleQAManagerChange(e.target.value)}
                  className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  data-testid="qa-manager-select"
                >
                  <option value="">-- Select QA Manager --</option>
                  {availableUsers.map(u => (
                    <option key={u.id} value={u.id}>
                      {u.name} ({u.email}) - {u.role}
                    </option>
                  ))}
                </select>
              </div>
              {settings?.qa_manager_email && (
                <div className="flex items-center gap-4 p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                  <CheckCircle className="h-6 w-6 text-green-500" />
                  <div>
                    <p className="font-medium text-white">{settings.qa_manager_name}</p>
                    <p className="text-sm text-green-400">{settings.qa_manager_email}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Department Managers Section */}
        <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
          <div className="p-4 bg-gradient-to-r from-amber-600/20 to-orange-600/20 border-b border-slate-700">
            <div className="flex items-center gap-3">
              <Building2 className="h-5 w-5 text-amber-400" />
              <h2 className="text-lg font-semibold text-white">Department Managers</h2>
            </div>
            <p className="text-sm text-slate-400 mt-1">
              Department managers receive document approval requests for their respective departments
            </p>
          </div>
          <div className="p-4 space-y-3">
            {bmsDepartments.map(dept => {
              const currentManager = getDepartmentManager(dept.code);
              const managerUser = availableUsers.find(u => u.id === currentManager);
              const isExpanded = expandedDepts[dept.code];
              
              return (
                <div key={dept.code} className="border border-slate-600 rounded-lg overflow-hidden">
                  <div 
                    className="flex items-center justify-between p-4 bg-slate-700/50 cursor-pointer hover:bg-slate-700 transition-colors"
                    onClick={() => toggleDeptExpand(dept.code)}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-mono bg-slate-600 px-2 py-1 rounded text-slate-300">
                        {dept.code}
                      </span>
                      <span className="font-medium text-white">{dept.name}</span>
                      {managerUser && (
                        <span className="text-sm text-green-400 flex items-center gap-1">
                          <CheckCircle className="h-4 w-4" />
                          {managerUser.name}
                        </span>
                      )}
                    </div>
                    {isExpanded ? (
                      <ChevronUp className="h-5 w-5 text-slate-400" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-slate-400" />
                    )}
                  </div>
                  {isExpanded && (
                    <div className="p-4 bg-slate-800/50 border-t border-slate-600">
                      <label className="block text-sm font-medium text-slate-300 mb-2">
                        Department Manager
                      </label>
                      <select
                        value={currentManager}
                        onChange={(e) => handleDepartmentManagerChange(dept.code, e.target.value)}
                        className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                        data-testid={`dept-manager-${dept.code}`}
                      >
                        <option value="">-- Not Configured --</option>
                        {availableUsers.map(u => (
                          <option key={u.id} value={u.id}>
                            {u.name} ({u.email})
                          </option>
                        ))}
                      </select>
                      {managerUser && (
                        <p className="mt-2 text-sm text-slate-400">
                          Notifications will be sent to: <span className="text-blue-400">{managerUser.email}</span>
                        </p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Notification Preferences */}
        <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
          <div className="p-4 bg-gradient-to-r from-green-600/20 to-teal-600/20 border-b border-slate-700">
            <div className="flex items-center gap-3">
              <Bell className="h-5 w-5 text-green-400" />
              <h2 className="text-lg font-semibold text-white">Notification Preferences</h2>
            </div>
            <p className="text-sm text-slate-400 mt-1">
              Enable or disable specific email notification types
            </p>
          </div>
          <div className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { key: 'send_document_submission_emails', label: 'Document Submissions', desc: 'When documents are submitted for approval' },
                { key: 'send_document_approval_emails', label: 'Document Approvals', desc: 'When documents are approved' },
                { key: 'send_document_rejection_emails', label: 'Document Rejections', desc: 'When documents are rejected' },
                { key: 'send_oncr_emails', label: 'ONCR Notifications', desc: 'Non-conformance alerts and updates' },
                { key: 'send_capa_emails', label: 'CAPA Notifications', desc: 'Corrective action assignments and updates' },
                { key: 'send_ncr_emails', label: 'NCR Notifications', desc: 'Non-conformance report alerts' },
                { key: 'cc_qa_manager_on_all_documents', label: 'CC QA Manager on All', desc: 'Copy QA Manager on all document emails' },
                { key: 'cc_submitter_on_approval', label: 'Notify Submitter', desc: 'Notify document submitter of approval status' }
              ].map(pref => (
                <label 
                  key={pref.key}
                  className="flex items-start gap-3 p-3 rounded-lg border border-slate-600 hover:border-slate-500 cursor-pointer transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={settings?.[pref.key] ?? true}
                    onChange={(e) => setSettings({ ...settings, [pref.key]: e.target.checked })}
                    className="mt-1 h-4 w-4 rounded border-slate-500 bg-slate-700 text-blue-600 focus:ring-blue-500"
                  />
                  <div>
                    <span className="font-medium text-white">{pref.label}</span>
                    <p className="text-xs text-slate-400">{pref.desc}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Test Email Section */}
        <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
          <div className="p-4 bg-gradient-to-r from-blue-600/20 to-cyan-600/20 border-b border-slate-700">
            <div className="flex items-center gap-3">
              <Send className="h-5 w-5 text-blue-400" />
              <h2 className="text-lg font-semibold text-white">Test Email</h2>
            </div>
            <p className="text-sm text-slate-400 mt-1">
              Send a test email to verify the configuration is working
            </p>
          </div>
          <div className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Recipient Name
                </label>
                <input
                  type="text"
                  value={testName}
                  onChange={(e) => setTestName(e.target.value)}
                  placeholder="John Doe"
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Recipient Email
                </label>
                <input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="email@example.com"
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500"
                  data-testid="test-email-input"
                />
              </div>
              <div className="flex items-end">
                <button
                  onClick={handleSendTestEmail}
                  disabled={sendingTest}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                  data-testid="send-test-email-btn"
                >
                  {sendingTest ? <RefreshCw className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
                  Send Test
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Email Logs Section */}
        <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
          <div className="p-4 border-b border-slate-700 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Settings className="h-5 w-5 text-slate-400" />
              <h2 className="text-lg font-semibold text-white">Email Logs</h2>
            </div>
            <button
              onClick={fetchEmailLogs}
              className="flex items-center gap-2 px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition-colors"
            >
              <RefreshCw className="h-4 w-4" />
              Load Logs
            </button>
          </div>
          {showLogs && (
            <div className="max-h-96 overflow-y-auto">
              {emailLogs.length === 0 ? (
                <div className="p-8 text-center text-slate-400">
                  No email logs found
                </div>
              ) : (
                <table className="w-full">
                  <thead className="bg-slate-700/50 sticky top-0">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Recipient</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Subject</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Sent</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-700">
                    {emailLogs.map((log, idx) => (
                      <tr key={idx} className="hover:bg-slate-700/30">
                        <td className="px-4 py-3">
                          <div className="text-sm text-white">{log.to_name}</div>
                          <div className="text-xs text-slate-400">{log.to_email}</div>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-300 max-w-xs truncate">
                          {log.subject}
                        </td>
                        <td className="px-4 py-3">
                          {log.status === 'sent' ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded-full">
                              <CheckCircle className="h-3 w-3" /> Sent
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-500/20 text-red-400 text-xs rounded-full">
                              <XCircle className="h-3 w-3" /> Failed
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-400">
                          {new Date(log.sent_at).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
